import React, { useState, useEffect } from 'react';
import { Breadcrumb, Collapse, Switch } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import {
  getSpecificNewsRequest,
  saveNewsRequest,
} from 'modules/newsModule/requests/newsRequests';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  DatePicker,
  ConfigProvider,
  BackTop,
  Select,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import TextEditor from 'modules/layoutModule/components/TextEditor/TextEditor';
import 'moment/locale/pl';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import dayjs from 'dayjs';
import locale from 'antd/locale/pl_PL';
import 'dayjs/locale/pl';
import copyToClipboard from 'helpers/copyToClipboard';

const { TextArea } = Input;
const { Panel } = Collapse;

const NewsEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [contentValue, setContentValue] = useState<string | undefined>('');
  const [isGalleryVisible, setGalleryVisibility] = useState(false);
  const [cover, setCover] = useState<string | null>(null);
  const [coverChanged, changeCover] = useState<boolean>(false);

  useEffect(() => {
    toggleLoading(true);
    getSpecificNewsRequest(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;
          const { rows } = data;
          const { categories } = data;

          setFields([
            {
              name: ['id'],
              value: rows.id,
            },
            {
              name: ['title'],
              value: rows.name,
            },
            {
              name: ['intro'],
              value: rows.intro,
            },
            {
              name: ['dateAdd'],
              value: dayjs(rows.dateAdd, 'YYYY/MM/DD HH:mm:ss'),
            },
            {
              name: ['content'],
              value: rows.content,
            },
            {
              name: ['idCategory'],
              value: rows.idCategory,
            },
            {
              name: ['image'],
              value: rows.image,
            },
            {
              name: ['alt1'],
              value: rows.alt1,
            },
            {
              name: ['isActive'],
              value: rows.isActive,
            },
            {
              name: ['table1'],
              value: rows.table1,
            },
            {
              name: ['table2'],
              value: rows.table2,
            },
            {
              name: ['table3'],
              value: rows.table3,
            },
            {
              name: ['table4'],
              value: rows.table4,
            },
            {
              name: ['table5'],
              value: rows.table5,
            },
          ]);
          setContentValue(rows.content);
          form.setFieldsValue({
            image: rows?.image,
          });
          setCover(rows?.image);
          setCategories(categories);
        } else {
          history.push('/news');
        }
      })
      .catch(() => {
        history.push('/news');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    saveNewsRequest(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const onContentEdit = (e: any) => {
    setFields([
      {
        name: ['content'],
        value: e,
      },
    ]);
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (value: string | null) => {
    form.setFieldsValue({
      image: value,
    });
    setCover(value);
    changeCover(true);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Aktualności</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/news">Lista newsów</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/news/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="image"
                label="Okładka"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={cover}
                  setCover={updateCover}
                  isNew={coverChanged}
                  id={form.getFieldsValue()?.id}
                  field="fileImage1"
                  number={1}
                />
              </Form.Item>
              <Form.Item name="alt1" label="Opis zdjęcia okładki">
                <Input />
              </Form.Item>
              <ConfigProvider locale={locale}>
                <Form.Item
                  name="dateAdd"
                  label="Data dodania"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  {/*// @ts-ignore*/}
                  <DatePicker showTime />
                </Form.Item>
              </ConfigProvider>
              <Form.Item
                name="idCategory"
                label="Kategoria"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Wybierz kategorię"
                  optionFilterProp="children"
                  options={categories}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      const label = option?.label as string;
                      return label.toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item
                name="title"
                label="Tytuł"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="content"
                label="Treść"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TextEditor
                  initialValue={contentValue}
                  onChange={onContentEdit}
                  disableGallery
                />
              </Form.Item>
              <div className="tables-collapse">
                <Collapse defaultActiveKey={[]}>
                  <Panel header="Tabelka 1" key="1" forceRender>
                    <Form.Item name="table1" label="Tabelka 1">
                      <TextArea rows={4} />
                    </Form.Item>
                    <Button
                      type="link"
                      onClick={() => copyToClipboard('TABELKA#1')}
                      style={{ padding: 0, margin: 0 }}
                    >
                      TABELKA#1
                    </Button>
                  </Panel>
                  <Panel header="Tabelka 2" key="2" forceRender>
                    <Form.Item name="table2" label="Tabelka 2">
                      <TextArea rows={4} />
                    </Form.Item>
                    <Button
                      type="link"
                      onClick={() => copyToClipboard('TABELKA#2')}
                      style={{ padding: 0, margin: 0 }}
                    >
                      TABELKA#2
                    </Button>
                  </Panel>
                  <Panel header="Tabelka 3" key="3" forceRender>
                    <Form.Item name="table3" label="Tabelka 3">
                      <TextArea rows={4} />
                    </Form.Item>
                    <Button
                      type="link"
                      onClick={() => copyToClipboard('TABELKA#3')}
                      style={{ padding: 0, margin: 0 }}
                    >
                      TABELKA#3
                    </Button>
                  </Panel>
                  <Panel header="Tabelka 4" key="4" forceRender>
                    <Form.Item name="table4" label="Tabelka 4">
                      <TextArea rows={4} />
                    </Form.Item>
                    <Button
                      type="link"
                      onClick={() => copyToClipboard('TABELKA#4')}
                      style={{ padding: 0, margin: 0 }}
                    >
                      TABELKA#4
                    </Button>
                  </Panel>
                  <Panel header="Tabelka 5" key="5" forceRender>
                    <Form.Item name="table5" label="Tabelka 5">
                      <TextArea rows={4} />
                    </Form.Item>
                    <Button
                      type="link"
                      onClick={() => copyToClipboard('TABELKA#5')}
                      style={{ padding: 0, margin: 0 }}
                    >
                      TABELKA#5
                    </Button>
                  </Panel>
                </Collapse>
              </div>
              <Form.Item
                name="isActive"
                label="Aktywność"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/news">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/news">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default NewsEdit;
